/**
 * The Setup page's one-liners and the helper table, formatted from a zone and `GET /tooling/info`.
 *
 * Pure, like `bridge-card.ts`: the decisions (which origin, which URL, how a digest map becomes rows)
 * are string arithmetic a test can call directly. Nothing here is a secret: the bootstrap URL carries
 * the zone's public id only, and the server renders the same script for everybody who asks.
 */

import type { ToolingInfo } from '@/api/types'

export interface InstallCommands {
  /** `https://host`, taken from the zone's own MCP URL so the one-liner names the host the client will reach. */
  origin: string
  shUrl: string
  ps1Url: string
  /** `curl -fsSL <shUrl> | sh` */
  sh: string
  /** `irm <ps1Url> | iex` */
  ps1: string
}

/** Characters a zone public id has (`z_` + base64url); anything else is not put into a command line. */
const PUBLIC_ID = /^z_[A-Za-z0-9_-]{1,30}$/

/** `undefined` when the MCP URL is not a URL or the id is not a public id: nothing is better than a wrong command. */
export function installCommands(mcpUrl: string, publicId: string): InstallCommands | undefined {
  if (!PUBLIC_ID.test(publicId)) return undefined
  let origin: string
  try {
    origin = new URL(mcpUrl).origin
  } catch {
    return undefined
  }
  const shUrl = `${origin}/i/${publicId}`
  const ps1Url = `${shUrl}.ps1`
  return { origin, shUrl, ps1Url, sh: `curl -fsSL ${shUrl} | sh`, ps1: `irm ${ps1Url} | iex` }
}

export interface HelperRow {
  /** `linux-amd64`, `windows-arm64`, … */
  target: string
  sha256: string
  /** The file name `GET /tooling/<file>` serves. */
  file: string
}

/** Rows for the helper table, sorted by target; empty when this build ships no helper. */
export function helperRows(
  info: Pick<ToolingInfo, 'helper_version' | 'helper_sha256'>,
): readonly HelperRow[] {
  const version = info.helper_version
  if (version === null || version === undefined) return []
  return Object.entries(info.helper_sha256 ?? {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([target, sha256]) => ({
      target,
      sha256,
      file: `saltseed-${version}-${target}${target.startsWith('windows-') ? '.exe' : ''}`,
    }))
}
